import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import DatePickerComponent from "../DatePickerComponent";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../../hooks/apiHook";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

const AddLeadModal = ({
  leadId,
  showAddLeadModal,
  setShowAddLeadModal,
  editFormData,
  setEditFormData,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [followDate, setFollowDate] = useState(editFormData?.followupDate);
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    description: "",
    followupDate: dayjs().format('YYYY-MM-DD'),
  });
  const [errorMessage, setErrorMessage] = useState({
    description: "",
    followupDate: "",
    selectedFile: "",
  });
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);
  const handleClose = () => {
    setShowAddLeadModal(false);
    // Clear form fields when modal is closed
    setEditFormData({
      id: "",
      firstName: "",
      lastName: "",
      emailId: "",
      skypeId: "",
      mobileNo: "",
      countryId: "",
      description: "",
      interestStatus: "",
      followupDate: null,
      leadStatus: "",
    });
  };
  const addFollowUpMutation = ApiHook.CallAddFollowUp();
  const openCalender = () => {
    setIsCalenderOpen(true);
  };
  const closeCalender = () => {
    setIsCalenderOpen(false);
  };
  const handleFileUpload = (e) => {
    const file = e.target.files;
    setFiles([...file]);
  };
  const handleSubmit = (event) => {
    event.preventDefault();

    const today = dayjs(); // Get today's date
    
    // Check if follow-up date is in the past
    if (dayjs(followDate).isBefore(today, 'day') || dayjs(followDate).isSame(today, 'day')) {
      // Display error message
      toast.error(t("Next follow-up date cannot be today or in the past"));
      return; // Stop further execution
    }

    // Add the 'Id' property to the formData
    const data = {
      ...formData,
      Id: leadId,
      files: files,
    };
    addFollowUpMutation.mutateAsync(data, {
      onSuccess: (res) => {
        if (res?.status) {
          toast.success(t(res?.data))
          queryClient.invalidateQueries({ queryKey: ['crm-tiles'] });
          queryClient.invalidateQueries({ queryKey: ['followup-today'] });
          queryClient.invalidateQueries({ queryKey: ['recent-leads'] });
          queryClient.invalidateQueries({ queryKey: ['missed-followup'] });
          queryClient.invalidateQueries({ queryKey: ['view-leads'] });
          setFiles([]);
          setFormData({
            description: "",
            followupDate: dayjs().format('YYYY-MM-DD'),
          })
        }
      },
    });

    handleClose();
  };
  useEffect(() => {
    editFormData?.followupDate ? setFollowDate(dayjs(editFormData?.followupDate)) : setFollowDate(null);
  }, [editFormData]);

  const handleChange = (event) => {
    const { id, value, files } = event.target;
    setFormData({
      ...formData,
      [id]: id === "selectedFile" ? files : value,
    });
    setErrorMessage((prev) => ({
      ...prev,
      [id]: null,
    }));
    const requiredIds = ["lead", "description", "nextFollowDate"];
  };
  const handleDateChange = (newDate) => {
    if (newDate) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      setFollowDate(newDate);
      setFormData((prev) => ({
        ...prev,
        followupDate: formattedDate,
      }));
      setErrorMessage((prev) => ({
        ...prev,
        nextFollowDate: null,
      }));
    }
  };
  return (
    <Modal
      id="addfollowup"
      show={showAddLeadModal}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('add_follow-up')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="form-group">
            <Form.Label>{t("lead")}</Form.Label>
            <Form.Control
              id="lead"
              type="text"
              placeholder=""
              value={editFormData?.firstName + " " + editFormData?.lastName}
              disabled
            />
            {errorMessage.lead && (
              <span className="validation-error-message">
                {errorMessage.lead}
              </span>
            )}
          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label>{t("description")}</Form.Label>
            <Form.Control
              id="description"
              as="textarea"
              type="text"
              placeholder=""
              value={formData.description}
              onChange={handleChange}
            />
            {errorMessage.description && (
              <span className="validation-error-message">
                {errorMessage.description}
              </span>
            )}
          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label>{t('followupDate')}</Form.Label>
            <DatePickerComponent
              className={"date-picker"}
              date={followDate}
              handleDateChange={handleDateChange}
              isCalenderOpen={isCalenderOpen}
              openCalender={openCalender}
              closeCalender={closeCalender}
              past={true}
            />
            {errorMessage.nextFollowDate && (
              <span className="validation-error-message">
                {errorMessage.nextFollowDate}
              </span>
            )}
          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label>{t('select_file')}</Form.Label>
            <Form.Control
              multiple
              id="selectedFile"
              type="file"
              placeholder=""
              onChange={handleFileUpload}
            />
            {errorMessage.selectedFile && (
              <span className="validation-error-message">
                {errorMessage.selectedFile}
              </span>
            )}
          </Form.Group>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              {t('close')}
            </Button>
            <Button variant="primary" type="submit">
              {t('add_follow-up')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddLeadModal;
